<template>
  <div
    v-if="
      recommendationsStatus === 'idle' || recommendationsStatus === 'pending'
    "
  >
    <RevSkeleton class="mb-32" height="32px" shape="rectangle" width="190px" />

    <RevSkeleton
      class="md:hidden"
      height="350px"
      shape="rectangle"
      width="100%"
    />
    <RevSkeleton
      class="hidden md:block"
      height="380px"
      shape="rectangle"
      width="100%"
    />
  </div>

  <div
    v-else-if="
      mainProduct && recommendedProductsWithDiscountedPrice && totalPrice
    "
  >
    <BlockTitle :title />
    <RevCard class="hidden py-24 md:block">
      <div class="flex">
        <div class="flex w-2/3 grow items-stretch justify-center">
          <div
            :ref="
              setupViewportTrackingProduct(mainProduct, {
                provider: 'product page',
                list: 'cross sell bundle',
              })
            "
            class="flex w-[200px] flex-col items-center lg:w-[254px]"
          >
            <BundledProduct
              :description="mainProduct.description"
              :discounted-price="mainProduct.discountedPrice"
              :horizontal="false"
              :image-src="mainProduct.image"
              :price="mainProduct.price"
              :price-new="mainProduct.priceNew"
              :title="mainProduct.title"
              :with-informations="true"
            />
          </div>
          <IconPlus class="mb-[133px] self-center" size="medium" />
          <div
            :ref="
              setupViewportTrackingProduct(
                recommendedProductsWithDiscountedPrice,
                {
                  provider: 'algolia',
                  list: 'cross sell bundle',
                },
              )
            "
            class="flex w-[200px] flex-col items-center lg:w-[254px]"
          >
            <RevButtonBase
              class="h-full"
              data-test="bundled-product"
              :to="`/${recommendedProductsWithDiscountedPrice.productPageLink.params.locale}/p/${recommendedProductsWithDiscountedPrice.productPageLink.params.slugV2}/${recommendedProductsWithDiscountedPrice.productPageLink.params.uuid}`"
            >
              <BundledProduct
                :description="
                  recommendedProductsWithDiscountedPrice.description
                "
                :discounted-price="
                  recommendedProductsWithDiscountedPrice.discountedPrice
                "
                :horizontal="false"
                :image-src="recommendedProductsWithDiscountedPrice.image"
                :price="recommendedProductsWithDiscountedPrice.price"
                :price-new="recommendedProductsWithDiscountedPrice.priceNew"
                :title="recommendedProductsWithDiscountedPrice.title"
                :with-informations="true"
                @click="handleBundleClick"
              />
            </RevButtonBase>
          </div>
        </div>
        <AddToCartBundleButton
          v-if="mainProduct"
          class="w-1/3 grow p-24"
          :main-product
          :recommended-product="recommendedProductsWithDiscountedPrice"
          :total-price="i18n.price(totalPrice)"
          @add-to-cart="handleBundleAddToCart"
        />
      </div>
    </RevCard>

    <RevCard v-if="mainProduct" class="flex flex-col gap-24 pt-24 md:hidden">
      <div class="flex items-center justify-center px-16">
        <div
          :ref="
            setupViewportTrackingProduct(mainProduct, {
              provider: 'product page',
              list: 'cross sell bundle',
            })
          "
        >
          <RevIllustration
            :alt="title"
            :height="88"
            :src="mainProduct.image"
            :width="88"
          />
        </div>
        <IconPlus class="shrink-0 px-4" size="medium" />
        <div
          :ref="
            setupViewportTrackingProduct(mainProduct, {
              provider: 'algolia',
              list: 'cross sell bundle',
            })
          "
        >
          <RevButtonBase
            :to="`/${recommendedProductsWithDiscountedPrice.productPageLink.params.locale}/p/${recommendedProductsWithDiscountedPrice.productPageLink.params.slugV2}/${recommendedProductsWithDiscountedPrice.productPageLink.params.uuid}`"
          >
            <RevIllustration
              :alt="title"
              :height="88"
              :src="recommendedProductsWithDiscountedPrice.image"
              :width="88"
              @click="handleBundleClick"
            />
          </RevButtonBase>
        </div>
      </div>
      <OpenBundleModalButton
        :main-product
        :recommended-product="recommendedProductsWithDiscountedPrice"
        :total-price="i18n.price(totalPrice)"
        @add-to-cart="handleBundleAddToCart"
      />
    </RevCard>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'

import { useDiscountedPrice } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useDiscountedPrice'
import BlockTitle from '@backmarket/nuxt-layer-cms/BlockTitle.vue'
import AddToCartBundleButton from '@backmarket/nuxt-layer-recommendation/AddToCartBundleButton.vue'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useAlgoliaRecommendations } from '@backmarket/nuxt-layer-recommendation/useAlgoliaRecommendations'
import { useImpressionTracking } from '@backmarket/nuxt-layer-recommendation/useProductTracking'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconPlus } from '@ds/icons/IconPlus'

import BundledProduct from './BundledProduct.vue'
import OpenBundleModalButton from './OpenBundleModalButton.vue'

const props = defineProps<{
  title: string
  mainProduct: Product
  widgetId?: string
}>()

const algoliaObjectId = computed(() => {
  if (!props.mainProduct.grade) return null

  return `${props.mainProduct.productId}_${props.mainProduct.grade.value}_0`
})

const { data: recommendations, status: recommendationsStatus } =
  useAlgoliaRecommendations(algoliaObjectId, 'bought-together', {
    maxRecommendations: 4,
  })

const recommendedProduct = computed(() =>
  recommendations.value && recommendations.value?.length > 0
    ? recommendations?.value[0]
    : null,
)

const totalPrice = computed(() =>
  recommendedProduct.value
    ? parseFloat(props.mainProduct.price.amount) +
      parseFloat(recommendedProduct.value.price.amount)
    : null,
)

const emit = defineEmits<{
  'add-to-cart': [Array<string>, Product | null]
}>()

const i18n = useI18n()

const { product: mainProduct } = useDiscountedPrice(toRef(props, 'mainProduct'))
const { product: recommendedProductsWithDiscountedPrice } =
  useDiscountedPrice(recommendedProduct)

const { trackProductClick } = useTracking()

function handleBundleClick() {
  if (recommendedProductsWithDiscountedPrice.value && mainProduct.value) {
    trackProductClick({
      list: 'cross sell bundle',
      widgetId: props.widgetId || '',
      product: {
        id: recommendedProductsWithDiscountedPrice.value.listingId,
        dealType: 'normal',
        position: 2,
        provider: 'algolia',
        source: 'recommendations',
        mainListing: mainProduct.value.listingId,
      },
    })
  }
}

function handleBundleAddToCart(listings: Array<string>) {
  emit('add-to-cart', listings, recommendedProduct.value)
}

const { setupViewportTrackingProduct } = useImpressionTracking()
</script>
